import { useState, useCallback, useEffect } from "react";
import { ScrollView } from "react-native";
import { View, Text } from "@src/components";
import { Stack, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { useCameraRouteParams } from "@src/hooks/useCameraRouteParams";
import { FormInput } from "@src/components/FormInput";
import { Button } from "@src/components/Button";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useUpdateCameraNickname } from "@src/services/api/hooks";
import { resolveCameraDisplayName } from "@src/features/cameras/utils/camera-display-name";
import { useCameraPermissions } from "@visioni/a3s-access/app";

export default function CameraDetailsScreen() {
  const { siteId, cameraKey, camera, isCameraLoading } = useCameraRouteParams({
    requireSiteId: true,
    requireCameraKey: true,
    fetchCamera: true,
  });

  const { theme } = useUnistyles();
  const router = useRouter();
  const updateNicknameMutation = useUpdateCameraNickname();
  const { canSetNickname, isLoading: permissionsLoading } = useCameraPermissions(siteId ?? "", cameraKey ?? "");

  // Use formatted camera label as the default value when no nickname is set.
  const cameraId = cameraKey ?? "";
  const currentNickname = resolveCameraDisplayName(cameraId, camera?.nickname);

  const [displayName, setDisplayName] = useState(currentNickname);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when camera data loads
  useEffect(() => {
    setDisplayName(resolveCameraDisplayName(cameraId, camera?.nickname));
    setHasChanges(false);
  }, [camera?.nickname, cameraId]);

  const handleDisplayNameChange = useCallback(
    (text: string) => {
      setDisplayName(text);
      setHasChanges(text !== currentNickname);
    },
    [currentNickname],
  );

  const handleSave = useCallback(() => {
    if (!cameraKey || !siteId) return;

    const nickname = displayName.trim() || null;

    updateNicknameMutation.mutate(
      {
        siteId,
        cameraKey,
        nickname,
      },
      {
        onSuccess: () => {
          showSuccessToast({ message: "Camera details updated", description: "Display name saved successfully" });
          router.back();
        },
        onError: (error: unknown) => {
          showErrorToast({ message: "Error", description: "Failed to update camera display name" });
          console.error("Update nickname error:", error);
        },
      },
    );
  }, [displayName, cameraKey, siteId, updateNicknameMutation, router]);

  const isLoading = isCameraLoading || updateNicknameMutation.isPending;

  return (
    <>
      <Stack.Screen
        options={{
          title: "Camera Details",
          headerStyle: {
            backgroundColor: theme.colors.background,
          },
          headerTintColor: theme.colors.text,
          headerBackButtonDisplayMode: "minimal",
        }}
      />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          padding: theme.spacing["5xl"],
          gap: theme.spacing["3xl"],
        }}
      >
        <View>
          <Text textStyle="sm/medium" color="text" mb="md">
            Display Name
          </Text>
          <Text textStyle="xs/regular" color="textMuted" mb="lg">
            {permissionsLoading || canSetNickname ?
              "Set a custom name for this camera to help identify it easily."
            : "You don't have permission to edit the display name."}
          </Text>
          <FormInput
            value={displayName}
            onChangeText={handleDisplayNameChange}
            placeholder="Camera display name"
            editable={!isLoading && !permissionsLoading && canSetNickname}
            autoCapitalize="words"
            maxLength={100}
          />
        </View>

        {!permissionsLoading && canSetNickname && (
          <View mt="xl">
            <Button
              onPress={handleSave}
              label="Save"
              variant="primary"
              size="md"
              loading={updateNicknameMutation.isPending}
              disabled={!hasChanges || isLoading}
            />
          </View>
        )}
      </ScrollView>
    </>
  );
}
